/**
 * roadmapSessions — datos de las 12 estaciones de la ruta de terapia de pareja.
 *
 * Fuente: docs/plan-ruta-terapeutica-pareja.md §7.1 (títulos, entregados por el
 * usuario 2026-09-10) y §7.2 (forma de `RoadmapStation`). Data pura, sin lógica —
 * el componente (`CoupleRoadmapMap`) decide cuál estación está bloqueada/actual/
 * completada a partir de `sesionActual`.
 *
 * `imagenes`: flyers del "método Conectemos" (2026-09-28), fuente PNG en
 * `comercial/método_conectemos/`, convertidos a webp 720×1280 (9:16)
 * en `public/assets/img/12sesiones_parejas/sesion-NN.webp`. Estación 7
 * (Asociación libre) usa el flyer "Atención flotante". Si un archivo falta, `CoupleRoadmapMap`
 * cae al placeholder numerado en `onError` (docs/spec_roadmap_pareja_map.md §7).
 *
 * `imagenes` (2026-09-28b): lista — más de una se muestra como carrusel en la
 * tarjeta de detalle. Estación 2 = flyer propio + "Los 6 pilares
 * fundamentales" (`sesion-02b.webp`, antes era la imagen de la estación 1).
 * Estación 1 = "Método Díaz — Sesión 1, indicaciones del ejercicio" (4:5,
 * 720×900 — se ve completa con franjas dentro del marco 9:16; fuente
 * `comercial/creativos-roadmap-pareja/sesion01_indicaciones.jpeg`) + "Las 4
 * preguntas claves" (`sesion-01b`). OJO: el flyer "Método Díaz para la terapia
 * de parejas" (12 estaciones + precio) NO va acá — es material de prospección
 * que se manda por chat.
 * `video`: YouTube opcional — desplegable arriba de las imágenes con link a
 * YouTube + reproductor embebido (youtube-nocookie), ver `RoadmapStationMedia`.
 *
 * `descripcion`: texto tomado del propio flyer de cada estación (contenido del
 * usuario, 2026-09-28) — no redactar contenido clínico nuevo acá.
 *
 * `perfiles` (2026-09-28c): información selectiva por miembro de la pareja.
 * Estación 4 muestra primero una portada ("Información para la consulta") con
 * dos tarjetas dibujadas en la imagen — Konex (hombre) y Vinka (mujer). Cada
 * tarjeta tiene un botón transparente encima (`zona`, en % de la imagen) que
 * abre el flyer de ese perfil; la flecha arriba-izquierda vuelve a la
 * portada. En la consulta el terapeuta pide que se pasen el celular para que
 * cada uno vea solo lo suyo — por eso la portada no muestra ninguno de los dos.
 *
 * `enlace`: CTA externo opcional debajo de la descripción (estación 12 =
 * reseña en Google).
 */

export interface RoadmapStation {
  numero: number; // 1..12
  titulo: string;
  descripcion: string; // párrafo para la tarjeta expandida — sale del texto del flyer
  imagenes: string[]; // ['/assets/img/12sesiones_parejas/sesion-01.webp', …] — >1 = carrusel
  video?: RoadmapVideo;
  perfiles?: RoadmapPerfiles; // si existe, reemplaza a `imagenes`
  enlace?: RoadmapEnlace;
}

export interface RoadmapVideo {
  youtubeId: string;
  titulo: string;
}

/** Zona clickeable sobre la portada, en % del ancho/alto de la imagen. */
export interface RoadmapZona {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface RoadmapPerfil {
  id: string;
  etiqueta: string; // accesible — la imagen ya trae el texto visible
  imagen: string;
  zona: RoadmapZona;
}

export interface RoadmapPerfiles {
  portada: string;
  /** Flecha dibujada arriba-izquierda en la portada → cierra el detalle y vuelve al mapa. */
  zonaVolver: RoadmapZona;
  opciones: RoadmapPerfil[];
}

export interface RoadmapEnlace {
  href: string;
  etiqueta: string;
}

function imagenSesion(numero: number, sufijo = ''): string {
  return `/assets/img/12sesiones_parejas/sesion-${String(numero).padStart(2, '0')}${sufijo}.webp`;
}

export const RUTA_PAREJA: RoadmapStation[] = [
  {
    numero: 1,
    titulo: 'Etimología',
    descripcion:
      'Trabajo individual antes de la consulta: vean juntos el video y anoten, cada uno por su lado, sus ideas centrales y lo que les llame la atención. Pongan especial atención al minuto 41/43, donde se habla de los pilares de las relaciones de pareja. Importante: no comenten el video con su pareja hasta la consulta.',
    imagenes: [imagenSesion(1), imagenSesion(1, 'b')],
    video: { youtubeId: 'u4duNKPm9cM', titulo: 'Video Dr. Riso' },
  },
  {
    numero: 2,
    titulo: 'Manual de convivencia familiar',
    descripcion:
      'Construyendo juntos una mejor relación: reconocer los 6 pilares fundamentales, darles conceptos y significados compartidos, y escribir normas lo más claras y transparentes posible, con límites sanos y sin tanto margen de interpretación.',
    imagenes: [imagenSesion(2), imagenSesion(2, 'b')],
  },
  {
    numero: 3,
    titulo: 'Emocionario',
    descripcion:
      'Un cuestionario para comprender, comunicar y fortalecer la relación. Úsenlo en pareja, reflexionen, conversen y den un nuevo paso hacia una relación más sana.',
    imagenes: [imagenSesion(3), imagenSesion(3, 'b')],
  },
  {
    numero: 4,
    titulo: 'Sistemas comunicativos',
    descripcion:
      'Este ejercicio te invita a observar, reconocer y organizar las formas, lo que favorece la comunicación, la expresión y la conexión con los demás.',
    imagenes: [],
    perfiles: {
      portada: imagenSesion(4, '-presentacion'),
      zonaVolver: { left: 5, top: 6, width: 11, height: 7 },
      opciones: [
        {
          id: 'hombre',
          etiqueta: 'Konex — información para él',
          imagen: imagenSesion(4, '-hombre'),
          zona: { left: 6.5, top: 48, width: 42, height: 37.5 },
        },
        {
          id: 'mujer',
          etiqueta: 'Vinka — información para ella',
          imagen: imagenSesion(4, '-mujer'),
          zona: { left: 51.5, top: 48, width: 42, height: 37.5 },
        },
      ],
    },
  },
  {
    numero: 5,
    titulo: 'Como si fuese la primera vez',
    descripcion:
      'Revive la emoción, la ilusión y la conexión de los primeros encuentros. A veces, el amor no necesita ser nuevo, solo necesita ser recordado.',
    imagenes: [imagenSesion(5)],
  },
  {
    numero: 6,
    titulo: 'Contrapunto',
    descripcion:
      'No debe existir miedo al conflicto, porque el soporte emocional y el descubrimiento del otro permiten que no todas las discusiones sean un campo de batalla.',
    imagenes: [imagenSesion(6)],
  },
  {
    numero: 7,
    titulo: 'Asociación libre',
    descripcion:
      'Una técnica terapéutica que permite escuchar, observar y comprender sin juzgar, para que puedas explorar lo que sientes, piensas y necesitas, en un espacio seguro y de confianza.',
    imagenes: [imagenSesion(7)],
  },
  {
    numero: 8,
    titulo: 'Mapa del amor',
    descripcion:
      'Trazar metas juntos también es una forma de amarse: metas de corto, mediano y largo plazo para fortalecer el ágape positivo.',
    imagenes: [imagenSesion(8)],
  },
  {
    numero: 9,
    titulo: 'Celos retrospectivos',
    descripcion:
      'Iniciar desde cero la relación no significa olvidar lo que vivimos, sino elegirnos de nuevo sin tratarnos como si fuésemos las personas que nos dañaron en el pasado.',
    imagenes: [imagenSesion(9)],
  },
  {
    numero: 10,
    titulo: 'Higiene sexual',
    descripcion:
      'Fortalece tu intimidad con respeto, comunicación y placer consciente. Tu bienestar también es parte del amor.',
    imagenes: [imagenSesion(10)],
  },
  {
    numero: 11,
    titulo: 'Perdón',
    descripcion:
      'Soltar el rencor, entregar y recibir el perdón, para restaurar la paz y la tranquilidad de la relación, fortaleciendo el ágape positivo.',
    imagenes: [imagenSesion(11)],
  },
  {
    numero: 12,
    titulo: 'Cierre del proceso — evaluación psicométrica de estabilidad final',
    descripcion:
      'Lo que construimos juntos también se evalúa: revisamos lo vivido, lo aprendido y lo que viene. Una relación sana no se improvisa… se construye, se evalúa y se fortalece.',
    imagenes: [imagenSesion(12)],
    enlace: {
      href: 'https://g.page/r/CXSjlA3XxwitEBM/review',
      etiqueta: 'Déjanos tu reseña en Google — Conectemos Chile',
    },
  },
];

export const TOTAL_SESIONES_PAREJA = RUTA_PAREJA.length;
